import { supabase } from "@/integrations/supabase/client";

interface RecommendationContext {
  userId: string;
  completedWorkflows?: string[];
  userTier?: string;
  unaPhase?: string;
  hasSpouse?: boolean;
  hasChildren?: boolean;
  hasLLC?: boolean;
}

/**
 * Recommend workflows based on user context and history
 */
export async function getWorkflowRecommendations(
  context: RecommendationContext
): Promise<string[]> {
  const recommendations: string[] = [];
  
  // Get user's completed workflows if not provided
  let completedWorkflows = context.completedWorkflows;
  if (!completedWorkflows) {
    const { data } = await supabase
      .from("workflow_completion_tracking")
      .select("workflow_id")
      .eq("user_id", context.userId)
      .not("completed_at", "is", null);
    
    completedWorkflows = data?.map((w) => w.workflow_id) || [];
  }

  // Get all available workflows
  const { data: workflows } = await supabase
    .from("workflows")
    .select("id, workflow_key, category, tier_restrictions")
    .eq("is_active", true);

  if (!workflows) return recommendations;

  // Filter by tier if specified
  const availableWorkflows = workflows.filter((w) => {
    if (context.userTier && w.tier_restrictions && w.tier_restrictions.length > 0) {
      return w.tier_restrictions.includes(context.userTier);
    }
    return true;
  });

  // Recommendation logic based on completed workflows
  for (const workflow of availableWorkflows) {
    // Skip if already completed
    if (completedWorkflows.includes(workflow.id)) continue;

    // Officer appointment → Banking authority
    if (
      completedWorkflows.some((id) =>
        workflows.find((w) => w.id === id && w.workflow_key === "add_una_officer")
      ) &&
      workflow.workflow_key === "grant_banking_authority"
    ) {
      recommendations.push(workflow.id);
    }

    // Spouse-related workflows
    if (context.hasSpouse && workflow.workflow_key === "add_una_member_simple") {
      recommendations.push(workflow.id);
    }

    // Children-related workflows
    if (context.hasChildren && workflow.workflow_key === "add_child_member") {
      recommendations.push(workflow.id);
    }

    // LLC-related workflows
    if (context.hasLLC && workflow.category === "asset_management") {
      recommendations.push(workflow.id);
    }

    // New UNA recommendations
    if (context.unaPhase === "new") {
      if (["add_una_member_simple", "add_una_officer"].includes(workflow.workflow_key)) {
        recommendations.push(workflow.id);
      }
    }

    // Estate planning for established UNAs
    if (context.unaPhase === "established" && workflow.category === "estate_planning") {
      recommendations.push(workflow.id);
    }
  }

  // Return unique recommendations, limit to 5
  return [...new Set(recommendations)].slice(0, 5);
}

/**
 * Get next logical workflow after completing one
 */
export async function getNextWorkflowSuggestion(
  completedWorkflowKey: string
): Promise<string[]> {
  const nextSteps: Record<string, string[]> = {
    add_una_officer: ["grant_banking_authority"],
    add_una_member_simple: ["add_child_member", "add_una_officer"],
    grant_banking_authority: ["transfer_real_estate"],
    add_child_member: ["add_una_member_simple"],
  };

  const suggestions = nextSteps[completedWorkflowKey] || [];
  
  // Get workflow IDs from keys
  const { data: workflows } = await supabase
    .from("workflows")
    .select("id")
    .in("workflow_key", suggestions)
    .eq("is_active", true);

  return workflows?.map((w) => w.id) || [];
}

/**
 * Track workflow abandonment (not touched in 7 days)
 */
export async function checkWorkflowAbandonment(userId: string): Promise<void> {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  // Find workflows not completed and not updated in 7 days
  const { data: abandonedWorkflows } = await supabase
    .from("workflow_completion_tracking")
    .select("id, workflow_id")
    .eq("user_id", userId)
    .is("completed_at", null)
    .lt("started_at", sevenDaysAgo.toISOString());

  if (abandonedWorkflows && abandonedWorkflows.length > 0) {
    // Mark as abandoned
    for (const wf of abandonedWorkflows) {
      await supabase
        .from("workflow_completion_tracking")
        .update({ abandoned: true })
        .eq("id", wf.id);

      // Track abandonment
      await supabase
        .from("content_usage_analytics")
        .insert({
          content_type: "workflow",
          content_id: wf.workflow_id,
          user_id: userId,
          action_type: "abandoned",
          metadata: { days_since_start: 7 },
        });
    }
  }
}
